import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { io } from 'socket.io-client';
import { API_BASE_URL } from '../config';
import '../styles/BattleshipSpectator.css';

const BriefcaseSpectatorPage = () => {
  const [searchParams] = useSearchParams();
  const isEmbed = searchParams.get('embed') === 'true';
  const [gameState, setGameState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [lastAwarded, setLastAwarded] = useState(null);
  const socketRef = useRef(null);
  const prevRanksRef = useRef({});
  const [isFs, setIsFs] = useState(false);
  const [activeAnimation, setActiveAnimation] = useState(null);
  const animTimeoutRef = useRef(null);

  useEffect(() => {
    const onChange = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const updateState = useCallback((data) => {
    if (!data) return;
    const state = data.state || data;
    if (!state.teams) return;

    const sortedTeams = [...state.teams].sort((a, b) =>
      (b.totalValue || 0) - (a.totalValue || 0)
    );

    const currentRanks = {};
    sortedTeams.forEach((team, index) => {
      const prevRank = prevRanksRef.current[team.id];
      currentRanks[team.id] = index + 1;
      team.rankChange = prevRank ? prevRank - (index + 1) : 0;
    });
    prevRanksRef.current = currentRanks;

    setGameState({ ...state, teams: sortedTeams });
    setLastUpdate(new Date());
  }, []);

  const fetchGameState = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/briefcase/state`);
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          updateState(data.state);
        }
      }
    } catch (err) {
      console.error('Error fetching briefcase state:', err);
    } finally {
      setLoading(false);
    }
  }, [updateState]);

  useEffect(() => {
    const socket = io(API_BASE_URL, {
      auth: { public: true },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      setConnected(true);
      fetchGameState();
    });

    socket.on('disconnect', () => {
      setConnected(false);
    });

    socket.on('briefcase-state-update', (data) => {
      updateState(data);
    });

    socket.on('scroll-event', (data) => {
      const briefcaseEvents = {
        'briefcase:right': { label: 'CORRECT!', color: '#00ff88', icon: '✅', style: 'bounce' },
        'briefcase:wrong': { label: 'WRONG!', color: '#ff4444', icon: '❌', style: 'shake' },
        'briefcase:double': { label: 'DOUBLED!', color: '#ffd700', icon: '💰', style: 'explode' },
        'briefcase:half': { label: 'HALVED!', color: '#ff6600', icon: '✂️', style: 'shake' },
        'briefcase:mystery': { label: 'MYSTERY!', color: '#aa00ff', icon: '❓', style: 'pulse' },
        'briefcase:suspense': { label: 'SUSPENSE...', color: '#ffaa00', icon: '😰', style: 'pulse' },
        'briefcase:reveal-good': { label: 'GREAT DEAL!', color: '#00ff88', icon: '🎉', style: 'explode' },
        'briefcase:reveal-bad': { label: 'BAD DEAL!', color: '#ff4444', icon: '💀', style: 'shake' },
        'game:question': { label: 'QUESTION TIME!', color: '#4488ff', icon: '❓', style: 'bounce' },
        'game:briefcase-steal': { label: 'STOLEN!', color: '#ff00ff', icon: '🕵️', style: 'spin' },
        'sprite:briefcase-run': { label: 'DEAL OR NO DEAL!', color: '#ffd700', icon: '💼', style: 'zoom' },
      };
      const anim = briefcaseEvents[data.type];
      if (anim) {
        setActiveAnimation({ ...anim, key: Date.now() });
        if (animTimeoutRef.current) clearTimeout(animTimeoutRef.current);
        const duration = (data.duration || 3) * 1000;
        animTimeoutRef.current = setTimeout(() => setActiveAnimation(null), duration);
      }
    });

    socket.on('briefcase-awarded', (data) => {
      setLastAwarded({
        teamId: data.teamId,
        teamName: data.teamName,
        value: data.briefcase?.value,
        timestamp: Date.now()
      });
      setTimeout(() => setLastAwarded(null), 4000);
    });

    fetchGameState();
    const fallbackInterval = setInterval(fetchGameState, 5000);

    return () => {
      socket.disconnect();
      clearInterval(fallbackInterval);
    };
  }, [fetchGameState, updateState]);

  const getRankBadge = (rank) => {
    switch (rank) {
      case 1: return { icon: '1ST', cls: 'gold' };
      case 2: return { icon: '2ND', cls: 'silver' };
      case 3: return { icon: '3RD', cls: 'bronze' };
      default: return { icon: `#${rank}`, cls: '' };
    }
  };

  const formatCurrency = (value) => {
    if (typeof value === 'string') return value;
    if (value < 0) return `-$${Math.abs(value).toLocaleString()}`;
    return `$${value.toLocaleString()}`;
  };

  const getBriefcaseIcon = (bc) => {
    switch (bc.type) {
      case 'positive': return '+';
      case 'negative': return '-';
      case 'curtain': return bc.revealed ? '?' : '?';
      case 'double': return 'x2';
      case 'half': return '/2';
      case 'swap': return '<>';
      default: return '$';
    }
  };

  const renderConnectionStatus = () => (
    <div className={`connection-status ${connected ? 'connected' : 'disconnected'}`}>
      <span className="connection-dot"></span>
      <span className="connection-text">{connected ? 'LIVE' : 'RECONNECTING...'}</span>
    </div>
  );

  const fullscreenBtn = !isFs && (
    <button onClick={() => document.documentElement.requestFullscreen()} style={{
      position: 'fixed', top: 8, left: 8, zIndex: 99999,
      background: 'rgba(0,255,65,0.15)', border: '1px solid #00ff41',
      color: '#00ff41', padding: '4px 10px', fontSize: '11px',
      fontFamily: 'Courier New, monospace', cursor: 'pointer', borderRadius: '3px',
    }}>FULLSCREEN</button>
  );

  const backNav = !isEmbed && (
    <div className="spectator-back-nav">
      <Link to="/spectate" className="back-link">&larr; Back to Spectate</Link>
    </div>
  );

  if (loading) {
    return (
      <div className="spectator-page loading">
        {fullscreenBtn}
        {backNav}
        <div className="loading-spinner"></div>
        <div className="loading-text">Connecting to A Brief Case...</div>
      </div>
    );
  }

  if (!gameState?.active) {
    return (
      <div className="spectator-page no-game">
        {fullscreenBtn}
        {backNav}
        {renderConnectionStatus()}
        <div className="no-game-icon" style={{ fontSize: '120px', opacity: 0.3 }}>&#128188;</div>
        <div className="no-game-text">No Active Game</div>
        <div className="no-game-subtext">Waiting for A Brief Case to start...</div>
        <div className="waiting-animation">
          <span className="waiting-dot"></span>
          <span className="waiting-dot"></span>
          <span className="waiting-dot"></span>
        </div>
      </div>
    );
  }

  const teams = gameState.teams || [];

  return (
    <div className="spectator-page">
      {fullscreenBtn}
      {backNav}
      {renderConnectionStatus()}

      <div className="spectator-header">
        <h1 className="game-title" style={{
          background: 'linear-gradient(90deg, #ffd700, #ffaa00, #ff8800, #ffd700)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
        }}>A BRIEF CASE</h1>
        <div className="status-banner playing">
          <span className="status-text">
            LIVE
          </span>
        </div>
      </div>

      {lastAwarded && (
        <div style={{
          textAlign: 'center', padding: 'clamp(12px, 1.5vh, 24px) clamp(24px, 3vw, 50px)',
          margin: '0 auto clamp(15px, 2vh, 30px)',
          background: 'rgba(255, 215, 0, 0.2)', border: '2px solid #ffd700',
          borderRadius: '10px', maxWidth: '800px',
          animation: 'winner-glow 1s ease-in-out infinite',
        }}>
          <span style={{ fontSize: 'clamp(22px, 2.5vw, 44px)', fontWeight: 'bold', color: '#ffd700' }}>
            {lastAwarded.teamName} won {formatCurrency(lastAwarded.value)}!
          </span>
        </div>
      )}

      <div style={{
        maxWidth: '1400px', margin: '0 auto', display: 'flex',
        flexDirection: 'column', gap: 'clamp(8px, 1vh, 16px)', padding: '0 30px',
      }}>
        {teams.map((team, index) => {
          const rank = index + 1;
          const badge = getRankBadge(rank);
          const justAwarded = lastAwarded?.teamId === team.id;

          return (
            <div key={team.id} style={{
              display: 'flex', alignItems: 'center', gap: 'clamp(12px, 1.5vw, 25px)',
              padding: 'clamp(14px, 1.5vh, 24px) clamp(20px, 2vw, 40px)',
              background: justAwarded
                ? 'rgba(255, 215, 0, 0.15)'
                : rank <= 3
                  ? 'rgba(255, 215, 0, 0.05)'
                  : 'rgba(30, 30, 40, 0.8)',
              border: justAwarded
                ? '2px solid #ffd700'
                : rank <= 3
                  ? '2px solid rgba(255, 215, 0, 0.3)'
                  : '2px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '12px',
              transition: 'all 0.5s ease',
              animation: justAwarded ? 'winner-glow 1s ease-in-out infinite' : 'none',
            }}>
              {/* Rank */}
              <div style={{
                minWidth: 'clamp(50px, 5vw, 80px)', textAlign: 'center',
                fontSize: rank <= 3 ? 'clamp(28px, 3vw, 52px)' : 'clamp(20px, 2vw, 36px)',
                fontWeight: 'bold', letterSpacing: '2px',
                color: rank === 1 ? '#ffd700' : rank === 2 ? '#c0c0c0' : rank === 3 ? '#cd7f32' : '#666',
              }}>
                {badge.icon}
              </div>

              {/* Team name */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: rank <= 3 ? 'clamp(24px, 2.5vw, 48px)' : 'clamp(20px, 2vw, 38px)',
                  fontWeight: 'bold', color: '#fff',
                  textTransform: 'uppercase', letterSpacing: 'clamp(2px, 0.3vw, 6px)',
                }}>
                  {team.name}
                </div>
                {team.briefcases && team.briefcases.length > 0 && (
                  <div style={{
                    display: 'flex', gap: '5px', marginTop: '8px',
                    flexWrap: 'wrap',
                  }}>
                    {team.briefcases.slice(-12).map((bc, i) => (
                      <span key={i} style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        width: 'clamp(22px, 2vw, 34px)', height: 'clamp(22px, 2vw, 34px)',
                        borderRadius: '4px', fontSize: 'clamp(10px, 1vw, 16px)',
                        fontWeight: 'bold',
                        background: bc.type === 'positive' ? 'rgba(0,255,0,0.2)'
                          : bc.type === 'negative' ? 'rgba(255,0,0,0.2)'
                          : 'rgba(255,255,255,0.1)',
                        color: bc.type === 'positive' ? '#44ff44'
                          : bc.type === 'negative' ? '#ff4444'
                          : '#aaa',
                        border: `1px solid ${bc.type === 'positive' ? 'rgba(0,255,0,0.3)'
                          : bc.type === 'negative' ? 'rgba(255,0,0,0.3)'
                          : 'rgba(255,255,255,0.2)'}`,
                      }}>
                        {getBriefcaseIcon(bc)}
                      </span>
                    ))}
                    {team.briefcases.length > 12 && (
                      <span style={{ color: '#666', fontSize: 'clamp(11px, 1vw, 16px)', alignSelf: 'center' }}>
                        +{team.briefcases.length - 12}
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Total value */}
              <div style={{ textAlign: 'right' }}>
                <div style={{
                  fontSize: rank <= 3 ? 'clamp(28px, 3vw, 56px)' : 'clamp(22px, 2.5vw, 44px)',
                  fontWeight: 'bold',
                  color: (team.totalValue || 0) >= 0 ? '#00ff88' : '#ff4444',
                  fontFamily: 'Courier New, monospace',
                }}>
                  {formatCurrency(team.totalValue || 0)}
                </div>
                <div style={{ fontSize: 'clamp(11px, 1vw, 18px)', color: '#666' }}>
                  {team.briefcases?.length || 0} briefcase{(team.briefcases?.length || 0) !== 1 ? 's' : ''}
                </div>
              </div>

              {/* Rank change */}
              {team.rankChange !== 0 && (
                <div style={{
                  minWidth: '40px', textAlign: 'center',
                  fontSize: 'clamp(14px, 1.5vw, 24px)', fontWeight: 'bold',
                  color: team.rankChange > 0 ? '#00ff88' : '#ff4444',
                }}>
                  {team.rankChange > 0 ? `+${team.rankChange}` : team.rankChange}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {activeAnimation && (
        <div key={activeAnimation.key} style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          pointerEvents: 'none', zIndex: 9999,
          animation: `golfFade ${activeAnimation.style === 'explode' ? '4s' : '3s'} ease-out forwards`,
          background: activeAnimation.style === 'explode' ? `radial-gradient(circle, ${activeAnimation.color}22 0%, transparent 70%)` : 'transparent',
        }}>
          <div style={{
            fontSize: activeAnimation.style === 'explode' ? 'clamp(100px, 12vw, 200px)' : 'clamp(80px, 10vw, 160px)',
            animation: `golf_${activeAnimation.style || 'bounce'} 0.8s ease-out`,
          }}>{activeAnimation.icon}</div>
          <div style={{
            fontSize: activeAnimation.label.length > 16 ? 'clamp(36px, 4vw, 72px)' : 'clamp(48px, 5vw, 96px)',
            fontWeight: 'bold',
            color: activeAnimation.color,
            fontFamily: 'Courier New, monospace',
            textShadow: `0 0 20px ${activeAnimation.color}`,
            letterSpacing: 'clamp(4px, 0.8vw, 12px)',
            animation: `golf_${activeAnimation.style || 'bounce'}_text 0.6s ease-out`,
          }}>{activeAnimation.label}</div>
        </div>
      )}

      <div className="spectator-footer">
        <span className="update-time">
          Last updated: {lastUpdate?.toLocaleTimeString()}
        </span>
      </div>

      <style>{`
        @keyframes golfFade {
          0% { opacity: 0; }
          10% { opacity: 1; }
          70% { opacity: 1; }
          100% { opacity: 0; }
        }
        @keyframes golf_bounce {
          0% { transform: scale(0) translateY(50px); }
          40% { transform: scale(1.3) translateY(-30px); }
          60% { transform: scale(0.9) translateY(10px); }
          80% { transform: scale(1.1) translateY(-5px); }
          100% { transform: scale(1) translateY(0); }
        }
        @keyframes golf_bounce_text {
          0% { transform: scale(0.3); opacity: 0; }
          50% { transform: scale(1.1); opacity: 1; }
          100% { transform: scale(1); }
        }
        @keyframes golf_shake {
          0% { transform: scale(0) rotate(0deg); }
          20% { transform: scale(1.2) rotate(-10deg); }
          40% { transform: scale(1) rotate(10deg); }
          60% { transform: scale(1.1) rotate(-5deg); }
          80% { transform: scale(1) rotate(3deg); }
          100% { transform: scale(1) rotate(0deg); }
        }
        @keyframes golf_shake_text {
          0% { transform: translateX(-20px); opacity: 0; }
          30% { transform: translateX(10px); opacity: 1; }
          60% { transform: translateX(-5px); }
          100% { transform: translateX(0); }
        }
        @keyframes golf_explode {
          0% { transform: scale(0); }
          30% { transform: scale(2); }
          50% { transform: scale(0.8); }
          70% { transform: scale(1.5); }
          100% { transform: scale(1); }
        }
        @keyframes golf_explode_text {
          0% { transform: scale(0); opacity: 0; letter-spacing: 0px; }
          50% { transform: scale(1.3); opacity: 1; letter-spacing: 20px; }
          100% { transform: scale(1); letter-spacing: 8px; }
        }
        @keyframes golf_fade {
          0% { transform: scale(1.5); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes golf_fade_text {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }
        @keyframes golf_pulse {
          0% { transform: scale(0); }
          25% { transform: scale(1.3); }
          50% { transform: scale(0.9); }
          75% { transform: scale(1.15); }
          100% { transform: scale(1); }
        }
        @keyframes golf_pulse_text {
          0% { opacity: 0; transform: scale(0.8); }
          50% { opacity: 1; transform: scale(1.1); }
          100% { transform: scale(1); }
        }
        @keyframes golf_spin {
          0% { transform: scale(0) rotate(-360deg); }
          60% { transform: scale(1.2) rotate(30deg); }
          100% { transform: scale(1) rotate(0deg); }
        }
        @keyframes golf_spin_text {
          0% { transform: rotateY(90deg); opacity: 0; }
          100% { transform: rotateY(0deg); opacity: 1; }
        }
        @keyframes golf_zoom {
          0% { transform: scale(5); opacity: 0; }
          40% { transform: scale(0.8); opacity: 1; }
          100% { transform: scale(1); }
        }
        @keyframes golf_zoom_text {
          0% { transform: scale(3); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default BriefcaseSpectatorPage;
